import React, { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import TemplateDataContext from "../../Contexts/TemplateData.context";
import { jsonKeyMapping } from "../../Config/Constants";
import { TemplateObject } from "../../Models/TemplateObject";
import "./ExpandableSidebar.css";

interface ExpandableSidebarProps {
  title?: string;
  description?: string;
}

const ExpandableSidebar: React.FC<ExpandableSidebarProps> = ({
  title: propTitle,
  description: propDescription,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dynamicTitle, setDynamicTitle] = useState<string>("");
  const [dynamicDescription, setDynamicDescription] = useState<string>("");

  const { state } = useContext(TemplateDataContext);
  const { activeTemplateSteps } = state;
  const { stepName } = useParams();


    const isReviewStep = stepName === "review-submit";

  // Load dynamic content from current step
  useEffect(() => {
    if (propTitle || propDescription) {
      setDynamicTitle(propTitle || "");
      setDynamicDescription(propDescription || "");
      return;
    }
  });

  useEffect(() => {
    if (isReviewStep) {
      setDynamicTitle("");
      setDynamicDescription("");
      setIsExpanded(false); 
      return;
    }


    const bodyDefaultTitle = (document.body.dataset || {}).tooltipDefaultTitle;
    const bodyDefaultDescription = (document.body.dataset || {}).tooltipDefaultDescription;
    if (bodyDefaultTitle || bodyDefaultDescription) {
      setDynamicTitle(bodyDefaultTitle || "");
      setDynamicDescription(bodyDefaultDescription || "");
        return; 
    }

    if (activeTemplateSteps.length > 0 && stepName) {
      const currentStep = activeTemplateSteps.find(
        (step: TemplateObject) => step[jsonKeyMapping.SLUGIFY] === stepName
      );
      if (currentStep) {
        const isIntroductoryScreen =
          !currentStep[jsonKeyMapping.FORM_GROUP] ||
          !Array.isArray(currentStep[jsonKeyMapping.FORM_GROUP]) ||
          currentStep[jsonKeyMapping.FORM_GROUP].length === 0;
        if (isIntroductoryScreen) {
          setDynamicTitle("");
          setDynamicDescription("");
          return;
        }
        // Skip overriding review step if dataset present
        if (stepName === "review-submit" && (bodyDefaultTitle || bodyDefaultDescription)) return;
        const helpContent = extractHelpContent(currentStep);
        setDynamicTitle(helpContent.title);
        setDynamicDescription(helpContent.description);
      } else {
        setDynamicTitle("Help");
        setDynamicDescription("No help information available for this step.");
      }
    } else {
      setDynamicTitle("Template Help");
      setDynamicDescription("Navigate through the template steps to see context-specific help.");
    }
  }, [activeTemplateSteps, stepName, propTitle, propDescription]);

  // Add/remove body class when sidebar state changes
  useEffect(() => {
    if (isExpanded && !isReviewStep) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove("sidebar-expanded");
    };
  }, [isExpanded, isReviewStep]);

  // Observe body dataset changes so the sidebar updates when ReviewSummary
  // writes tooltip defaults to `document.body.dataset` after mount.
  useEffect(() => {
        if (isReviewStep) return;

    const body = document.body;
    if (!body) return;

    const handleDatasetChange = () => {
      if (propTitle || propDescription) return; // parent overrides
      const t = (body.dataset || {}).tooltipDefaultTitle;
      const d = (body.dataset || {}).tooltipDefaultDescription;
      if (t || d) {
        setDynamicTitle(t || "");
        setDynamicDescription(d || "");
      }
    };

    // Initial read in case dataset was set before mount
    handleDatasetChange();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes") {
          const attr = (m as any).attributeName;
          if (
            attr === "data-tooltip-default-title" ||
            attr === "data-tooltip-default-description"
          ) {
            handleDatasetChange();
            break;
          }
        }
      }
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: [
        "data-tooltip-default-title",
        "data-tooltip-default-description",
      ],
    });

    return () => observer.disconnect();
  }, [propTitle, propDescription]);


   if (isReviewStep) {
    return null;
  }

  // Extract help information from current step (tooltips, subtooltips, FAQs)
  const extractHelpContent = (currentStep: TemplateObject) => {
    // First priority: Check if there are form fields with subtooltips (most specific help)
    if (
      currentStep[jsonKeyMapping.FORM_GROUP] &&
      currentStep[jsonKeyMapping.FORM_GROUP].length > 0
    ) {
      const allSubtooltips: Array<{ title: string; description: string }> = [];

      // Collect all subtooltips from all form fields in the step
      const collectSubtooltips = (fields: any[]) => {
        fields.forEach((field: any) => {
          if (field.subtooltip && field.subtooltip.trim()) {
            // Extract title from HTML (between <span class='headline'> tags)
            const titleMatch = field.subtooltip.match(
              /<span class=['"]headline['"]>(.*?)<\/span>/
            );
            const title = titleMatch ? titleMatch[1] : field.label || "Help";

            // Extract description (text after <br> tag, removing HTML)
            const descriptionMatch = field.subtooltip.match(/<br>(.*)/);
            let description = descriptionMatch
              ? descriptionMatch[1].replace(/<[^>]*>/g, "").trim()
              : field.subtooltip.replace(/<[^>]*>/g, "").trim();

            // If the description starts with the same text as the title, remove that duplication
            if (title && description.startsWith(title)) {
              description = description.substring(title.length).trim();
            }

            allSubtooltips.push({ title, description });
          }

          // Check nested fields (like morefields in radio buttons)
          if (field.inputRadio && Array.isArray(field.inputRadio)) {
            field.inputRadio.forEach((radioOption: any) => {
              if (
                radioOption.morefields &&
                Array.isArray(radioOption.morefields)
              ) {
                collectSubtooltips(radioOption.morefields);
              }
            });
          }

          // Check other nested field types
          if (field.morefields && Array.isArray(field.morefields)) {
            collectSubtooltips(field.morefields);
          }
        });
      };

      collectSubtooltips(currentStep[jsonKeyMapping.FORM_GROUP]);

      if (allSubtooltips.length > 0) {
        // If single subtooltip, use the headline as title
        if (allSubtooltips.length === 1) {
          return {
            title: allSubtooltips[0].title,
            description: allSubtooltips[0].description,
          };
        } else {
          // Multiple subtooltips: render each with headline styling + description styling
          const combinedDescription = allSubtooltips
            .map((item, index) => `**${item.title}**${item.description}`)
            .join("\n\n");

          return {
            title: "", // No main title, headlines will be in the description as bold
            description: combinedDescription,
          };
        }
      }

      // Second priority: Check for regular tooltips in form fields
      const fieldsWithTooltips = currentStep[jsonKeyMapping.FORM_GROUP].filter(
        (field: any) => field.tooltip && field.tooltip.trim()
      );

      if (fieldsWithTooltips.length > 0) {
        const field = fieldsWithTooltips[0];
        return {
          title: field.label || "Help",
          description: field.tooltip.replace(/<[^>]*>/g, "").trim(),
        };
      }
    }

    // Third priority: Check if there are FAQs for this step
    if (currentStep.faq && currentStep.faq.length > 0) {
      const firstFaq = currentStep.faq[0];
      return {
        title: firstFaq.header || "FAQ",
        description: (
          firstFaq.Text ||
          firstFaq.text ||
          "No FAQ content available."
        )
          .replace(/<[^>]*>/g, "")
          .trim(),
      };
    }

    // Fourth priority: Check step-level tooltip
    if (currentStep.tooltip && currentStep.tooltip.trim()) {
      return {
        title: currentStep[jsonKeyMapping.SLUG] || "Help",
        description: currentStep.tooltip.replace(/<[^>]*>/g, "").trim(),
      };
    }

    // Fifth priority: Step description
    if (
      currentStep[jsonKeyMapping.DESCRIPTION] &&
      currentStep[jsonKeyMapping.DESCRIPTION].trim()
    ) {
      return {
        title: currentStep[jsonKeyMapping.SLUG] || "Information",
        description: currentStep[jsonKeyMapping.DESCRIPTION],
      };
    }

    // Final fallback
    return {
      description: "",
    };
  };

  // Helper function to render text with bold formatting separated from subtitle container
  // Helper: format inline bold markers (**bold**) into React nodes
  const formatInline = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        const boldText = part.slice(2, -2);
        return (
          <b key={`bold-${index}`} className="force-bold">
            {boldText}
          </b>
        );
      }
      // Keep whitespace/newlines when rendering; parent containers control block behavior
      return (
        <span key={`text-${index}`} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  // Render description text, converting newline-prefixed bullets into an unordered list
  const renderDescription = (text: string) => {
    // Normalize CRLF
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    // Split into paragraphs by double newlines
    const paragraphs = normalized
      .split(/\n\n+/g)
      .map((p) => p.trim())
      .filter(Boolean);

    return (
      <div className="subtitle subtooltip-group">
        {paragraphs.map((para, pidx) => {
          const lines = para
            .split(/\n+/)
            .map((l) => l.trim())
            .filter(Boolean);
          // If all lines start with a bullet marker (• or -) treat as a list
          const isList = lines.every((l) => /^([•\-*]|\u2022)\s+/.test(l));
          if (isList) {
            return (
              <ul
                className="subtooltip subtitle"
                key={`list-${pidx}`}
                style={{ paddingLeft: 20, margin: "8px 0" }}
              >
                {lines.map((l, lidx) => {
                  const cleaned = l.replace(/^([•\-*]|\u2022)\s+/, "");
                  return (
                    <li key={`li-${pidx}-${lidx}`} style={{ marginBottom: 8 }}>
                      {formatInline(cleaned)}
                    </li>
                  );
                })}
              </ul>
            );
          }

          // Otherwise render as a paragraph (allow inline bold markers)
          return (
            <div
              className="subtooltip subtitle"
              key={`para-${pidx}`}
              style={{ marginBottom: 12 }}
            >
              {formatInline(para)}
            </div>
          );
        })}
      </div>
    );
  };

  // Always render the sidebar, but leave content area empty if no help

  const toggleSidebar = () => {
    if (isReviewStep) return;
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className={`expandable-sidebar ${isExpanded ? "expanded" : "collapsed"}`}
           style={{
        visibility: isReviewStep ? "hidden" : "visible",
        pointerEvents: isReviewStep ? "none" : "auto",
      }}
    >
      <div className="frame-parent">
        <div
          className="frame-wrapper"
          onClick={toggleSidebar}
          id="expandable-sidebar-toggle"
          role="button"
          aria-expanded={isExpanded}
          aria-controls="expandable-sidebar-panel"
        >
          <img
            className={`frame-child ${isExpanded ? "arrow-icon" : "tips-icon"}`}
            alt=""
            src={
              isExpanded
                ? process.env.PUBLIC_URL +
                  "/static/images/icon/collapse_arrow.svg"
                : process.env.PUBLIC_URL + "/static/images/icon/tips.svg"
            }
          />
        </div>

        {isExpanded && (
          <div className="text-parent" id="expandable-sidebar-panel">
            {dynamicTitle && <b className="title">{dynamicTitle}</b>}
            {dynamicDescription && renderDescription(dynamicDescription)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandableSidebar;
